// =============================================================================
// Skill Studio (/studio) — edit skills dynamically — newspaper theme
// =============================================================================

import { stateful, useState, useEffect } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { listSkills, saveSkillOp, deleteSkillOp, listConnections } from '../../db/analytics-resolvers.js';
import { newspaperStyle, masthead, NP } from './newspaper.js';

interface Skill {
  id: string;
  title: string;
  icon: string;
  desc: string;
  model: string;
  tools: string[];
  connections: string[];
  prompt: string;
}

const MODELS = ['default', 'haiku', 'sonnet', 'opus'];
const TOOL_LABELS: Record<string, string> = { mongo: 'MongoDB (read-only)', web: 'Web search & fetch' };
const blank = (): Skill => ({ id: '', title: '', icon: '', desc: '', model: 'default', tools: ['mongo', 'web'], connections: ['mainDB'], prompt: '' });

export const StudioPage = stateful(() => {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [editing, setEditing] = useState<Skill | null>(null);
  const [isNew, setIsNew] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [msg, setMsg] = useState<string>('');
  const [conns, setConns] = useState<string[]>([]);

  const load = async () => {
    try {
      setSkills(JSON.parse(await listSkills()()));
    } catch {
      /* ignore */
    }
    try {
      setConns((JSON.parse(await listConnections()()) as Array<{ name: string }>).map((c) => c.name));
    } catch {
      /* ignore */
    }
  };
  useEffect(() => {
    void load();
  }, []);

  // Always produce a complete, well-shaped Skill (arrays guaranteed) so the
  // edit form can never crash on a partial/legacy record.
  const normalize = (s: Partial<Skill>): Skill => ({
    ...blank(),
    ...s,
    tools: Array.isArray(s.tools) ? s.tools.filter((t) => t === 'mongo' || t === 'web') : ['mongo', 'web'],
    connections: Array.isArray(s.connections) ? s.connections : [],
  });
  const editSkill = (s: Skill) => {
    setEditing(normalize(s));
    setIsNew(false);
    setMsg('');
  };
  const newSkill = () => {
    setEditing(blank());
    setIsNew(true);
    setMsg('');
  };
  // NOTE: @adornis/functional-lit useState does NOT support functional updaters
  // (setState stores its argument verbatim). Always pass a concrete value, read
  // from the closure's current `editing` (the component body re-runs each render).
  const upd = (k: keyof Skill, v: any) => {
    if (editing) setEditing({ ...editing, [k]: v });
  };
  const toggleTool = (t: string) => {
    if (!editing) return;
    const cur = Array.isArray(editing.tools) ? editing.tools : [];
    setEditing({ ...editing, tools: cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t] });
  };

  const slugify = (s: string) =>
    (s || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60);

  const save = async () => {
    if (!editing) return;
    // ID is required, but fall back to a slug of the Title so an empty ID is
    // never a silent dead-end.
    let id = (editing.id || '').trim();
    if (!id) id = slugify(editing.title);
    if (!id) {
      setMsg('Δώσε ID ή Title');
      return;
    }
    const payload: Skill = { ...normalize(editing), id };
    setSaving(true);
    setMsg('');
    try {
      const res: string = await saveSkillOp(JSON.stringify(payload))();
      if (res === 'OK') {
        setMsg('Saved ✓');
        setEditing({ ...payload }); // reflect the resolved id back into the form
        setIsNew(false);
        await load();
      } else {
        setMsg(res || 'ERROR');
      }
    } catch (e: any) {
      setMsg(String(e?.message ?? e));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id: string) => {
    setSaving(true);
    try {
      await deleteSkillOp(id)();
      await load();
      if (editing?.id === id) setEditing(null);
    } catch {
      /* ignore */
    } finally {
      setSaving(false);
    }
  };

  // -- form helpers ----------------------------------------------------------
  const sectionTitle = (n: string, label: string) => html`
    <div class="flex items-center gap-2 mb-3 mt-1">
      <span class="flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold" style="background:#1c170f; color:#f3ead4; font-family:'Playfair Display',serif;">${n}</span>
      <span class="uppercase tracking-widest text-xs font-semibold" style="color:#5a4a32;">${label}</span>
      <span class="flex-1 h-px" style="background:rgba(40,30,18,.2);"></span>
    </div>
  `;
  const field = (label: string, hint: string, control: unknown) => html`
    <label class="block">
      <span class="block text-xs font-semibold mb-1" style="color:#3a2c16;">${label}</span>
      ${control}
      ${hint ? html`<span class="block text-[11px] italic mt-1" style="color:#8a7a5e;">${hint}</span>` : nothing}
    </label>
  `;
  const inputCls = 'input input-bordered input-sm w-full bg-transparent';

  return html`
    ${newspaperStyle(NP.PAPER_DARK)}
    <div class="np-wrap">
      <div class="container mx-auto px-4 py-6 max-w-6xl">
        ${masthead('Skill Studio', 'The composing room — configure the agents')}

        <div class="flex items-center mb-4">
          <p style="font-family:'Old Standard TT',Georgia,serif; color:#5a4a32;">Edit what each column does — the prompt sent to Claude, the model, the tools.</p>
          <button class="btn np-btn btn-sm ml-auto" @click=${newSkill}>+ New skill</button>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
          ${skills.map(
            (s) => html`
              <div class="np-card rounded-lg cursor-pointer transition-transform hover:-translate-y-0.5 ${editing?.id === s.id ? 'ring-2 ring-amber-800' : ''}" @click=${() => editSkill(s)}>
                <div class="p-4">
                  <div class="font-semibold text-lg" style="font-family:'Playfair Display',Georgia,serif;">${s.title}</div>
                  ${s.desc ? html`<div class="text-xs mt-0.5 mb-1.5" style="color:#6b5c44;">${s.desc}</div>` : nothing}
                  <div class="flex flex-wrap gap-1">
                    <span class="badge badge-ghost badge-sm">${s.id}</span>
                    <span class="badge badge-outline badge-sm">${s.model}</span>
                    ${(s.tools ?? []).map((t) => html`<span class="badge badge-sm" style="background:#2b2b2b;color:#f3ead4;border:none;">${t}</span>`)}
                  </div>
                </div>
              </div>
            `,
          )}
        </div>

        ${editing && !Array.isArray(editing)
          ? html`
              <div class="np-card rounded-lg overflow-hidden">
                <!-- sticky header -->
                <div class="flex items-center gap-2 px-5 py-3" style="background:#1c170f; color:#f3ead4;">
                  <h3 class="font-semibold text-lg" style="font-family:'Playfair Display',Georgia,serif; color:#f3ead4 !important;">
                    ${isNew ? 'New skill' : editing.title || editing.id}
                  </h3>
                  ${!isNew ? html`<span class="badge badge-sm ml-1" style="background:#3a3026;color:#d8c9a8;border:none;">${editing.id}</span>` : nothing}
                  <button class="btn btn-ghost btn-xs ml-auto" style="color:#d8c9a8;" @click=${() => setEditing(null)}>✕ close</button>
                </div>

                <div class="p-6 space-y-5">
                  <!-- 1. Identity -->
                  <section>
                    ${sectionTitle('1', 'Identity')}
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                      ${field('ID', isNew ? 'lowercase, no spaces — permanent' : 'cannot be changed', html`
                        <input class=${inputCls} .value=${editing.id} ?disabled=${!isNew}
                          @input=${(e: Event) => upd('id', (e.target as HTMLInputElement).value)} />`)}
                      <div class="md:col-span-2">
                        ${field('Title', 'shown on the button', html`
                          <input class=${inputCls} .value=${editing.title}
                            @input=${(e: Event) => upd('title', (e.target as HTMLInputElement).value)} />`)}
                      </div>
                    </div>
                    <div class="mt-3">
                      ${field('Description', 'one line, shown under the title', html`
                        <input class=${inputCls} .value=${editing.desc}
                          @input=${(e: Event) => upd('desc', (e.target as HTMLInputElement).value)} />`)}
                    </div>
                  </section>

                  <!-- 2. Configuration -->
                  <section>
                    ${sectionTitle('2', 'Configuration')}
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                      ${field('Model', 'which Claude runs the skill', html`
                        <select class="select select-bordered select-sm w-full bg-transparent" @change=${(e: Event) => upd('model', (e.target as HTMLSelectElement).value)}>
                          ${MODELS.map((m) => html`<option value=${m} ?selected=${editing!.model === m}>${m}</option>`)}
                        </select>`)}
                      <div>
                        <span class="block text-xs font-semibold mb-1" style="color:#3a2c16;">Tools</span>
                        <div class="flex flex-col gap-1.5 pt-1">
                          ${['mongo', 'web'].map(
                            (t) => html`<label class="flex items-center gap-2 text-sm cursor-pointer">
                              <input type="checkbox" class="checkbox checkbox-sm" ?checked=${(editing!.tools ?? []).includes(t)} @change=${() => toggleTool(t)} />
                              ${TOOL_LABELS[t] ?? t}</label>`,
                          )}
                        </div>
                      </div>
                      ${field('Connections', conns.length ? `comma-separated refs (resolved server-side) · available: ${conns.join(', ')}` : 'comma-separated refs (resolved to secrets server-side)', html`
                        <input class=${inputCls} .value=${(editing.connections ?? []).join(', ')}
                          @input=${(e: Event) => upd('connections', (e.target as HTMLInputElement).value.split(',').map((x) => x.trim()).filter(Boolean))} />`)}
                    </div>
                  </section>

                  <!-- 3. Prompt -->
                  <section>
                    ${sectionTitle('3', 'Prompt')}
                    ${field('', 'what is sent to Claude — the data dictionary, source links and memory rules are appended automatically', html`
                      <textarea class="textarea textarea-bordered font-mono text-xs w-full bg-transparent leading-relaxed" rows="12" .value=${editing.prompt}
                        @input=${(e: Event) => upd('prompt', (e.target as HTMLTextAreaElement).value)}></textarea>`)}
                  </section>
                </div>

                <!-- sticky action bar -->
                <div class="flex items-center gap-2 px-6 py-3" style="background:rgba(28,23,15,.06); border-top:1px solid rgba(40,30,18,.2);">
                  <button class="btn np-btn btn-sm" ?disabled=${saving} @click=${save}>${saving ? 'Saving…' : 'Save skill'}</button>
                  <button class="btn btn-ghost btn-sm" @click=${() => setEditing(null)}>Cancel</button>
                  ${msg ? html`<span class="text-sm ml-1" style="color:${msg.includes('✓') ? '#2d6a4f' : '#c53030'};">${msg}</span>` : nothing}
                  ${!isNew ? html`<button class="btn btn-error btn-outline btn-sm ml-auto" ?disabled=${saving} @click=${() => del(editing!.id)}>Delete</button>` : nothing}
                </div>
              </div>
            `
          : nothing}
      </div>
    </div>
  `;
});
